import { Grid, Typography, Box, Fab, TextField } from '@mui/material'
import React from 'react'
import { useFormContext, useWatch, Controller } from 'react-hook-form'
import { useStrings } from '../../assets/localization/strings'
import ReactHookFormSelect, { IOption } from '../common/form/ReactHookFormSelect'
import ReactHookFormSelectRedesign from '../common/form/ReactHookFormSelectRedesign'
import { IUGCCampaign } from './UGCCampaignForm'
import AddIcon from '@mui/icons-material/Add'
import CampaignImageExample from '../../assets/images/campaign_image_example.png'
import { PhotoFormat } from '../../models/GeneralTypes'
import { useState } from 'react'
import rootStore from '../../stores/rootStore'
import { getCurrencySymbolByCountry } from '../../reusableUtils/currency'

const options: IOption[] = [...Array(6)].map((_, i) => i).map(i => ({ label: `${i}`, value: i }))
const formatOptions: IOption[] = Object.entries(PhotoFormat).map(([key, value]) => ({
  value: value,
  label: `${key} (${value})`
}))

const CampaignImages = ({ isReadOnly }) => {
  const { control, setValue } = useFormContext<IUGCCampaign>()
  const numberOfPhotos = useWatch({ control, name: 'numberOfPhotos' })
  const photos = useWatch({ control, name: 'photos' }) || 0
  const strings = useStrings()

  const carouselImages = [
    'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/public/slider.jpg',
    'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/public/slider-2.jpg',
    'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/public/slider-3.jpg'
  ]

  const [currentIndex, setCurrentIndex] = useState(0)

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % carouselImages.length)
    }, 3000) // Change image every 3 seconds

    return () => clearInterval(interval) // Cleanup interval on unmount
  }, [])

  React.useEffect(() => {
    const photoCount = Number(numberOfPhotos) || 0
    const currentPhotos = photos || []

    if (currentPhotos.length < photoCount) {
      const newPhotos = [...currentPhotos]
      for (let i = currentPhotos.length; i < photoCount; i++) {
        newPhotos.push({ description: '', format: PhotoFormat.Standard })
      }
      setValue('photos', [...newPhotos])
    } else if (currentPhotos.length > photoCount) {
      setValue('photos', currentPhotos.slice(0, photoCount))
    }
  }, [numberOfPhotos, photos, setValue])

  const handleAddPhoto = () => {
    const newCount = Number(numberOfPhotos) + 1
    setValue('numberOfPhotos', newCount)

    const newPhotos = photos ? [...photos] : []
    newPhotos.push({ description: '', format: PhotoFormat.Square })
    setValue('photos', newPhotos)
  }

  const renderPhotoInputs = () => {
    const photoCount = Number(numberOfPhotos) || 0
    return Array.from({ length: photoCount }, (_, i) => (
      <Grid container spacing={2} key={i} sx={{ mt: 1 }}>
        <Grid item xs={12} md={8}>
          <Controller
            name={`photos.${i}.description`}
            render={({ field, fieldState: { error } }) => (
              <TextField
                required
                multiline
                size='small'
                fullWidth
                name={`photos.${i}.description`}
                variant='outlined'
                value={photos[i]?.description || ''}
                label={`${strings.campaign_photo_label} ${i + 1}`}
                error={error && !!error.ref.name}
                helperText={error && error.message}
                onChange={e => {
                  if (Array.isArray(photos)) {
                    const newPhotos = [...photos]
                    newPhotos[i] = { ...newPhotos[i], description: e.target.value }
                    setValue('photos', newPhotos)
                  }
                }}
                InputProps={{
                  inputProps: {
                    style: { padding: '7.5px' }
                  }
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ReactHookFormSelect
            name={`photos.${i}.format`}
            label='Format'
            options={formatOptions.map(c => ({
              value: c.value,
              label: c.label
            }))}
          />
        </Grid>
      </Grid>
    ))
  }

  const currencySymbol = rootStore.brandStore.selectedBrand?.brandCountry ? getCurrencySymbolByCountry(rootStore.brandStore.selectedBrand?.brandCountry) : '€'

  return (
    <>
      <Typography sx={{ fontFamily: 'Bricolage Grotesque', fontSize: '18px', fontWeight: 500, mt: '24px', mb: '4px' }}>{strings.campaign_order_photos_title}</Typography>
      <Grid container sx={{ py: '24px', px: '45px', borderRadius: '16px', borderStyle: 'solid', borderColor: '#0000001A', backgroundColor: '#fff' }}>
        <Grid item xs={12}>
          <Typography fontSize={'13px'} fontWeight={300} sx={{ mb: 1 }}>
            {strings.campaign_order_photos_description}
          </Typography>
        </Grid>
        <div style={{ width: '100%', marginTop: '46px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {/* "Number" pill with arrows */}
          <Grid item xs={12} sm={4}>
            <ReactHookFormSelectRedesign label={strings.campaign_photos_label(currencySymbol)} name='numberOfPhotos' options={options} disabled={isReadOnly} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* Image Display */}
              <Box sx={{ width: '100%', maxWidth: 350 }}>
                <Box
                  component='img'
                  src={carouselImages[currentIndex]}
                  alt={`Campaign Example ${currentIndex + 1}`}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: 10,
                    display: 'block',
                    transition: 'opacity 0.8s ease-in-out'
                  }}
                />
              </Box>

              {/* Navigation Dots */}
              <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                {carouselImages.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: currentIndex === index ? '#666' : '#ccc',
                      transition: 'background-color 0.3s'
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Grid>
        </div>
        {/* Photo fields if user selected at least 1 */}
        {numberOfPhotos > 0 && (
          <>
            <Grid item xs={12} sx={{ mt: 3 }}>
              <Typography variant='body1' sx={{ fontWeight: 500 }}>
                {strings.campaign_order_photos_text_field}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {renderPhotoInputs()}
            </Grid>
          </>
        )}

        {/* Add Photo Button */}
        {numberOfPhotos < 5 && numberOfPhotos > 0 && !isReadOnly && (
          <Grid item xs={12} textAlign='center' mt={2}>
            <Fab color='primary' aria-label='add' size='small' onClick={handleAddPhoto}>
              <AddIcon />
            </Fab>
          </Grid>
        )}
      </Grid>
    </>
  )
}

export default CampaignImages
